import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AccountingShell } from "../layout/AccountingShell";
import { DashboardPage } from "../../modules/dashboard/DashboardPage";
import { EntryPage } from "../../modules/entries/EntryPage";
import { cashEntryConfig, dailyEntryConfig, statementEntryConfig } from "../../modules/entries/entryConfigs";
import { LoginPage } from "../../modules/LoginPage";
import { ResourcePage } from "../../modules/masters/ResourcePage";
import { resourceConfigs } from "../../modules/masters/resourceConfigs";
import { TaxSetupPage } from "../../modules/masters/TaxSetupPage";
import { BankPage } from "../../modules/masters/BankPage";
import { PostingProfilesPage } from "../../modules/masters/PostingProfilesPage";
import { ReportsPage } from "../../modules/reports/ReportsPage";
import { QuoteGeneratorPage } from "../../modules/reports/QuoteGeneratorPage";
import { BillEntryPage } from "../../modules/vouchers/BillEntryPage";
import { UsersPage } from "../../modules/users/UsersPage";
import { RolesPage } from "../../modules/users/RolesPage";
import { PayrollPage } from "../../modules/payroll/PayrollPage";
import { useAuthStore } from "../../stores/authStore";
import { Result } from "antd";
import { hasPermission } from "../../shared/utils/permissions";

function Shell() {
  return (
    <AccountingShell>
      <Outlet />
    </AccountingShell>
  );
}

function ProtectedShell() {
  const token = useAuthStore((x) => x.accessToken);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Shell />;
}

function RequirePermission({ permission, children }: { permission?: string; children: JSX.Element }) {
  if (!hasPermission(permission)) {
    return <Result status="403" title="403" subTitle="You do not have permission to access this page." />;
  }
  return children;
}

export function AppRoutes() {
  const token = useAuthStore((x) => x.accessToken);
  return (
    <Routes>
      <Route path="/login" element={token ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={token ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={token ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route element={<ProtectedShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="/daily-entry" element={<RequirePermission permission="DailyEntry.Read"><EntryPage config={dailyEntryConfig} /></RequirePermission>} />
        <Route path="/cash-entry" element={<RequirePermission permission="CashEntry.Read"><EntryPage config={cashEntryConfig} /></RequirePermission>} />
        <Route path="/statement-entry" element={<RequirePermission permission="StatementEntry.Read"><EntryPage config={statementEntryConfig} /></RequirePermission>} />
        <Route path="/bill-entry" element={<RequirePermission permission="BillEntry.Read"><BillEntryPage title="All Vouchers" /></RequirePermission>} />
        <Route path="/bill-entry/sales" element={<RequirePermission permission="BillEntry.Read"><BillEntryPage title="Sales Invoice" voucherType="SalesInvoice" /></RequirePermission>} />
        <Route path="/bill-entry/purchase" element={<RequirePermission permission="BillEntry.Read"><BillEntryPage title="Purchase Invoice" voucherType="PurchaseInvoice" /></RequirePermission>} />
        <Route path="/bill-entry/receipt" element={<RequirePermission permission="BillEntry.Read"><BillEntryPage title="Receipt Voucher" voucherType="ReceiptVoucher" /></RequirePermission>} />
        <Route path="/bill-entry/payment" element={<RequirePermission permission="BillEntry.Read"><BillEntryPage title="Payment Voucher" voucherType="PaymentVoucher" /></RequirePermission>} />
        <Route path="/bill-entry/journal" element={<RequirePermission permission="BillEntry.Read"><BillEntryPage title="Journal Entry" voucherType="JournalEntry" /></RequirePermission>} />
        <Route path="/reports" element={<RequirePermission permission="Reports.Read"><ReportsPage /></RequirePermission>} />
        <Route path="/reports/ledger-vouchers" element={<RequirePermission permission="Reports.Read"><ReportsPage title="Ledger Vouchers" /></RequirePermission>} />
        <Route path="/reports/quote" element={<RequirePermission permission="Quote.Create"><QuoteGeneratorPage /></RequirePermission>} />
        <Route path="/masters/party" element={<ResourcePage config={resourceConfigs.party} />} />
        <Route path="/masters/bank" element={<BankPage />} />
        <Route path="/masters/short-remarks" element={<RequirePermission permission="ShortRemark.Read"><ResourcePage config={resourceConfigs["short-remarks"]} /></RequirePermission>} />
        <Route path="/masters/sac-codes" element={<ResourcePage config={resourceConfigs["sac-codes"]} />} />
        <Route path="/masters/financial-years" element={<ResourcePage config={resourceConfigs["financial-years"]} />} />
        <Route path="/masters/ledgers" element={<ResourcePage config={resourceConfigs.ledgers} />} />
        <Route path="/masters/tax-setup" element={<TaxSetupPage />} />
        <Route path="/masters/posting-profiles" element={<PostingProfilesPage />} />
        <Route path="/masters/line-items" element={<ResourcePage config={resourceConfigs["line-items"]} />} />
        <Route path="/masters/bill-units" element={<ResourcePage config={resourceConfigs["bill-units"]} />} />
        <Route path="/masters/organizations" element={<ResourcePage config={resourceConfigs.organizations} />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/roles" element={<RolesPage />} />
        <Route path="/payroll" element={<RequirePermission permission="Payroll.Read"><PayrollPage /></RequirePermission>} />
      </Route>
      <Route path="*" element={<Navigate to={token ? "/" : "/login"} replace />} />
    </Routes>
  );
}
